import React from 'react';

/**
 * To-scale visual representation of a video-wall grid. Each cell is a screen,
 * sized to the configured orientation aspect ratio so a 1×5 portrait strip and
 * a 2×2 landscape grid look physically distinct.
 *
 * Props:
 *   rows, columns   — grid dimensions
 *   orientation     — 'portrait' | 'landscape' (per-screen aspect)
 *   maxWidth        — bounding box width in px (height derived)
 *   renderCell(r,c,position) — optional cell content renderer (1-based position)
 *   cellStyle(r,c,position)  — optional per-cell style override
 */
export default function GridPreview({
  rows = 1,
  columns = 1,
  orientation = 'landscape',
  maxWidth = 240,
  renderCell,
  cellStyle,
}) {
  const r = Math.max(1, rows);
  const c = Math.max(1, columns);
  // Per-screen aspect (width:height). Portrait screen = 9:16, landscape = 16:9.
  const screenW = orientation === 'portrait' ? 9 : 16;
  const screenH = orientation === 'portrait' ? 16 : 9;

  const wallW = c * screenW;
  const wallH = r * screenH;
  const scale = maxWidth / wallW;
  const width = wallW * scale;
  const height = wallH * scale;

  return (
    <div
      // Physical screen layout — pin LTR so RTL (Arabic) never mirrors the grid
      // (cell 1 must stay top-left regardless of language direction).
      dir="ltr"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${c}, 1fr)`,
        gridTemplateRows: `repeat(${r}, 1fr)`,
        gap: 4,
        width,
        height,
        padding: 4,
        background: 'var(--pp-bg-tertiary)',
        borderRadius: 8,
      }}
    >
      {Array.from({ length: r * c }).map((_, idx) => {
        const row = Math.floor(idx / c);
        const col = idx % c;
        const position = idx + 1;
        return (
          <div
            key={idx}
            style={{
              background: 'var(--pp-bg-primary)',
              border: '1px solid var(--pp-border-strong)',
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: Math.max(10, Math.min(18, scale * screenH * 0.4)),
              fontWeight: 700,
              color: 'var(--pp-text-secondary)',
              overflow: 'hidden',
              ...(cellStyle ? cellStyle(row, col, position) : null),
            }}
          >
            {renderCell ? renderCell(row, col, position) : position}
          </div>
        );
      })}
    </div>
  );
}
